import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Star, MapPin, BadgeCheck, ChevronRight, Clock, Users,
  IndianRupee, Trophy, Briefcase, TrendingUp, Navigation, Sparkles
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { CAProfileDetail } from "@/components/marketplace/CAProfileDetail";
import { type CAProfile } from "@/components/marketplace/CACard";
import { useNavigate } from "react-router-dom";


function StarRating({ rating }: { rating: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((s) => (
        <Star
          key={s}
          className={`h-3 w-3 ${s <= Math.round(rating) ? "fill-yellow-400 text-yellow-400" : "text-muted-foreground/30"}`}
        />
      ))}
      <span className="text-xs font-medium text-foreground ml-1">{rating.toFixed(1)}</span>
    </div>
  );
}

function MiniCACard({ ca, onSelect }: { ca: CAProfile; onSelect: () => void }) {
  return (
    <motion.div whileHover={{ y: -2 }} transition={{ duration: 0.15 }}>
      <Card
        className="bg-card border-border hover:border-accent/30 transition-all cursor-pointer group h-full"
        onClick={onSelect}
      >
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 font-bold text-base bg-primary/10 text-primary">
              {ca.name.charAt(0)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                <h3 className="text-sm font-semibold text-foreground truncate">{ca.name}</h3>
                {ca.is_verified && <BadgeCheck className="h-3.5 w-3.5 text-accent flex-shrink-0" />}
              </div>
              {ca.firm_name && (
                <p className="text-[11px] text-muted-foreground truncate">{ca.firm_name}</p>
              )}
              <div className="flex items-center gap-2 mt-1">
                <span className="text-[10px] text-muted-foreground flex items-center gap-0.5">
                  <MapPin className="h-2.5 w-2.5" /> {ca.city}
                </span>
                <span className="text-[10px] text-muted-foreground flex items-center gap-0.5">
                  <Clock className="h-2.5 w-2.5" /> {ca.experience_years}y
                </span>
              </div>
            </div>
            <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-accent transition-colors flex-shrink-0 mt-1" />
          </div>
          <div className="flex items-center justify-between mt-3 pt-2 border-t border-border/30">
            <StarRating rating={ca.avg_rating} />
            {ca.pricing_hourly && (
              <span className="text-[10px] font-medium text-foreground flex items-center gap-0.5">
                <IndianRupee className="h-2.5 w-2.5" />{ca.pricing_hourly.toLocaleString("en-IN")}/hr
              </span>
            )}
          </div>
          <div className="flex flex-wrap gap-1 mt-2">
            {ca.specializations.slice(0, 2).map((s) => (
              <Badge key={s} variant="secondary" className="text-[9px] px-1.5 py-0 h-4">{s}</Badge>
            ))}
            {ca.specializations.length > 2 && (
              <Badge variant="outline" className="text-[9px] px-1.5 py-0 h-4">+{ca.specializations.length - 2}</Badge>
            )}
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}

interface DiscoverySectionProps {
  title: string;
  icon: React.ReactNode;
  description: string;
  cas: CAProfile[];
  onSelect: (ca: CAProfile) => void;
  accentColor?: string;
}

function DiscoverySection({ title, icon, description, cas, onSelect, accentColor = "text-accent" }: DiscoverySectionProps) {
  if (cas.length === 0) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className={accentColor}>{icon}</div>
          <div>
            <h2 className="text-base font-semibold text-foreground">{title}</h2>
            <p className="text-[11px] text-muted-foreground">{description}</p>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        {cas.slice(0, 4).map((ca) => (
          <MiniCACard key={ca.id} ca={ca} onSelect={() => onSelect(ca)} />
        ))}
      </div>
    </motion.div>
  );
}

const CADiscoveryPage = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [allProfiles, setAllProfiles] = useState<CAProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCA, setSelectedCA] = useState<CAProfile | null>(null);

  useEffect(() => {
    fetchProfiles();
  }, []);

  const fetchProfiles = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("ca_profiles")
      .select("*")
      .order("avg_rating", { ascending: false });

    if (error) {
      toast({ title: "Error loading profiles", description: error.message, variant: "destructive" });
    } else {
      setAllProfiles((data as CAProfile[]) || []);
    }
    setLoading(false);
  };

  const topRated = allProfiles
    .filter((p) => p.avg_rating >= 3.0 && p.total_reviews >= 1)
    .sort((a, b) => b.avg_rating - a.avg_rating)
    .slice(0, 4);

  const mostExperienced = [...allProfiles]
    .sort((a, b) => b.experience_years - a.experience_years)
    .slice(0, 4);

  const recentlyActive = [...allProfiles]
    .sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime())
    .slice(0, 4);

  // Group by city for "nearby" — show the most popular cities
  const cityCounts = allProfiles.reduce<Record<string, number>>((acc, p) => {
    acc[p.city] = (acc[p.city] || 0) + 1;
    return acc;
  }, {});
  const topCities = Object.entries(cityCounts)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 6)
    .map(([city]) => city);

  const nearbyCAs = topCities.length > 0
    ? allProfiles.filter((p) => p.city === topCities[0]).slice(0, 4)
    : [];

  const verifiedCAs = allProfiles.filter((p) => p.is_verified).slice(0, 4);

  if (selectedCA) {
    return <CAProfileDetail ca={selectedCA} onBack={() => setSelectedCA(null)} />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-display text-foreground flex items-center gap-2">
            <Sparkles className="h-6 w-6 text-accent" />
            Discover CAs
          </h1>
          <p className="text-sm text-muted-foreground">
            Browse top Chartered Accountants curated for your business needs
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => navigate("/dashboard/ca-marketplace")}>
          <Briefcase className="h-3.5 w-3.5 mr-1.5" /> Full Marketplace
        </Button>
      </div>

      {/* Stats Banner */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: "Total CAs", value: allProfiles.length, icon: <Users className="h-4 w-4" /> },
          { label: "Verified", value: allProfiles.filter((p) => p.is_verified).length, icon: <BadgeCheck className="h-4 w-4" /> },
          { label: "Cities", value: Object.keys(cityCounts).length, icon: <MapPin className="h-4 w-4" /> },
          { label: "Avg Rating", value: allProfiles.length > 0 ? (allProfiles.reduce((s, p) => s + p.avg_rating, 0) / allProfiles.length).toFixed(1) : "0", icon: <Star className="h-4 w-4" /> },
        ].map((stat) => (
          <Card key={stat.label} className="bg-card border-border">
            <CardContent className="p-3 flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-accent/10 flex items-center justify-center text-accent">
                {stat.icon}
              </div>
              <div>
                <p className="text-lg font-bold text-foreground">{stat.value}</p>
                <p className="text-[10px] text-muted-foreground uppercase tracking-wider">{stat.label}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i} className="bg-card border-border animate-pulse">
              <CardContent className="p-4 h-[160px]" />
            </Card>
          ))}
        </div>
      ) : allProfiles.length === 0 ? (
        <div className="text-center py-16">
          <Users className="h-10 w-10 text-muted-foreground/30 mx-auto mb-3" />
          <h3 className="text-sm font-semibold text-foreground mb-1">No CAs registered yet</h3>
          <p className="text-xs text-muted-foreground">Check back soon as professionals join the platform</p>
        </div>
      ) : (
        <div className="space-y-8">
          <DiscoverySection
            title="Top Rated CAs"
            icon={<Trophy className="h-5 w-5" />}
            description="Highest rated by businesses on the platform"
            cas={topRated}
            onSelect={setSelectedCA}
            accentColor="text-yellow-500"
          />

          <DiscoverySection
            title="Most Experienced"
            icon={<TrendingUp className="h-5 w-5" />}
            description="Decades of expertise across industries"
            cas={mostExperienced}
            onSelect={setSelectedCA}
            accentColor="text-primary"
          />

          {verifiedCAs.length > 0 && (
            <DiscoverySection
              title="Verified Professionals"
              icon={<BadgeCheck className="h-5 w-5" />}
              description="Identity and credentials verified by AccouPro"
              cas={verifiedCAs}
              onSelect={setSelectedCA}
              accentColor="text-accent"
            />
          )}

          {nearbyCAs.length > 0 && (
            <DiscoverySection
              title={`Popular in ${topCities[0]}`}
              icon={<Navigation className="h-5 w-5" />}
              description={`${nearbyCAs.length}+ CAs available in ${topCities[0]}`}
              cas={nearbyCAs}
              onSelect={setSelectedCA}
              accentColor="text-emerald-500"
            />
          )}

          <DiscoverySection
            title="Recently Active"
            icon={<Clock className="h-5 w-5" />}
            description="CAs who recently updated their profiles"
            cas={recentlyActive}
            onSelect={setSelectedCA}
            accentColor="text-muted-foreground"
          />
        </div>
      )}
    </div>
  );
};

export default CADiscoveryPage;
